import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { CheckCircle2, Clock, Circle, AlertTriangle, ArrowLeft, Award } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  approved: { icon: CheckCircle2, color: '#16A34A', label: 'Approved' },
  completed: { icon: CheckCircle2, color: '#16A34A', label: 'Completed' },
  in_progress: { icon: Clock, color: '#0047FF', label: 'In Progress' },
  rejected: { icon: AlertTriangle, color: '#DC2626', label: 'Rejected' },
  failed: { icon: AlertTriangle, color: '#DC2626', label: 'Failed' },
  pending: { icon: Circle, color: '#E5E7EB', label: 'Pending' },
};

const EVAL_CRITERIA = [
  { key: 'product_knowledge', label: 'Product Knowledge' },
  { key: 'empathy_communication', label: 'Empathy & Communication' },
  { key: 'energy_attitude', label: 'Energy & Attitude' },
  { key: 'professionalism', label: 'Professionalism' },
  { key: 'discipline', label: 'Discipline' },
  { key: 'problem_solving', label: 'Problem Solving' },
];

export default function TraineeDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trainee, setTrainee] = useState(null);
  const [structure, setStructure] = useState([]);
  const [progress, setProgress] = useState([]);
  const [remarks, setRemarks] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Progress update form
  const [editDay, setEditDay] = useState(null);
  const [editStatus, setEditStatus] = useState('pending');
  const [editObs, setEditObs] = useState('');
  const [editSales, setEditSales] = useState(0);

  // Remark form
  const [remarkContent, setRemarkContent] = useState('');
  const [remarkType, setRemarkType] = useState('remark');
  const [remarkDay, setRemarkDay] = useState('');

  // Evaluation form
  const [evalDay, setEvalDay] = useState('');
  const [evalScores, setEvalScores] = useState({});
  const [evalStatus, setEvalStatus] = useState('pass');
  const [evalNotes, setEvalNotes] = useState('');

  const canEdit = ['master_admin', 'admin', 'manager', 'trainer'].includes(user?.role);
  const canEvaluate = ['master_admin', 'admin', 'manager'].includes(user?.role);

  const loadData = useCallback(async () => {
    try {
      const [structRes, progRes, remRes, evalRes, usersRes] = await Promise.all([
        axios.get(`${API}/journey/structure`),
        axios.get(`${API}/journey/progress/${id}`),
        axios.get(`${API}/remarks/${id}`),
        axios.get(`${API}/evaluations/${id}`),
        axios.get(`${API}/users`),
      ]);
      setStructure(structRes.data);
      setProgress(progRes.data);
      setRemarks(remRes.data);
      setEvaluations(evalRes.data);
      const t = usersRes.data.find(u => u.id === id);
      setTrainee(t);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleProgressUpdate = async () => {
    if (!editDay) return;
    try {
      await axios.put(`${API}/journey/progress/${id}/${editDay}`, {
        status: editStatus, observations: editObs, sales_count: editSales
      });
      toast.success(`Day ${editDay} progress updated`);
      setEditDay(null);
      loadData();
    } catch (err) {
      toast.error('Failed to update progress');
    }
  };

  const handleRemarkSubmit = async (e) => {
    e.preventDefault();
    if (!remarkContent.trim()) return;
    try {
      await axios.post(`${API}/remarks`, {
        trainee_id: id, day: remarkDay ? parseInt(remarkDay) : null,
        content: remarkContent, remark_type: remarkType
      });
      toast.success('Remark added');
      setRemarkContent('');
      setRemarkDay('');
      loadData();
    } catch (err) {
      toast.error('Failed to add remark');
    }
  };

  const handleEvalSubmit = async (e) => {
    e.preventDefault();
    if (!evalDay) return;
    try {
      await axios.post(`${API}/evaluations`, {
        trainee_id: id, day: parseInt(evalDay), scores: evalScores,
        overall_status: evalStatus, notes: evalNotes
      });
      toast.success('Evaluation saved');
      setEvalDay('');
      setEvalScores({});
      setEvalNotes('');
      loadData();
    } catch (err) {
      toast.error('Failed to save evaluation');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0047FF] border-t-transparent animate-spin" /></div>;
  }

  const completedDays = progress.filter(p => p.status === 'completed' || p.status === 'approved').length;
  const pct = Math.round((completedDays / 16) * 100);
  const isGraduated = completedDays >= 16;

  return (
    <div data-testid="trainee-detail-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button data-testid="back-button" onClick={() => navigate(-1)} className="p-2 text-[#4B5563] hover:text-[#0A0A0A] transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] tracking-tight">{trainee?.name || 'Trainee'}</h1>
            {isGraduated && (
              <Badge className="rounded-none bg-[#16A34A] text-white font-body text-xs gap-1">
                <Award size={12} /> Graduated
              </Badge>
            )}
          </div>
          <p className="font-body text-sm text-[#4B5563]">@{trainee?.username} | Trainer: {trainee?.assigned_trainer_name || 'Unassigned'}</p>
        </div>
        {isGraduated && (
          <Button data-testid="view-certificate-button" onClick={() => navigate(`/certificate/${id}`)} variant="outline" className="rounded-none font-body gap-2">
            <Award size={16} /> View Certificate
          </Button>
        )}
      </div>

      {/* Progress bar */}
      <div className="border border-[#E5E7EB] p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="font-body text-sm font-semibold text-[#0A0A0A]">Overall Progress</span>
          <span className="font-body text-sm text-[#4B5563]">{completedDays}/16 days ({pct}%)</span>
        </div>
        <Progress value={pct} className="h-2" />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="progress" className="w-full">
        <TabsList className="rounded-none border border-[#E5E7EB] bg-[#F9FAFB] p-0 h-auto">
          <TabsTrigger data-testid="tab-progress" value="progress" className="rounded-none font-body text-sm data-[state=active]:bg-white data-[state=active]:shadow-none px-6 py-2.5">Progress</TabsTrigger>
          <TabsTrigger data-testid="tab-remarks" value="remarks" className="rounded-none font-body text-sm data-[state=active]:bg-white data-[state=active]:shadow-none px-6 py-2.5">Remarks ({remarks.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-evaluations" value="evaluations" className="rounded-none font-body text-sm data-[state=active]:bg-white data-[state=active]:shadow-none px-6 py-2.5">Evaluations ({evaluations.length})</TabsTrigger>
        </TabsList>

        {/* Progress Tab */}
        <TabsContent value="progress" className="mt-4 space-y-3">
          {structure.map((day) => {
            const prog = progress.find(p => p.day === day.day);
            const status = prog?.status || 'pending';
            const StatusIcon = STATUS_CONFIG[status]?.icon || Circle;
            const statusColor = STATUS_CONFIG[status]?.color || '#E5E7EB';
            const isEditing = editDay === day.day;

            return (
              <div key={day.day} data-testid={`progress-day-${day.day}`} className="border border-[#E5E7EB] p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <StatusIcon size={20} style={{ color: statusColor }} />
                    <span className="font-heading text-lg font-black text-[#0A0A0A]">{String(day.day).padStart(2, '0')}</span>
                    <span className="font-body text-sm font-medium text-[#0A0A0A]">{day.title}</span>
                    <Badge variant="outline" className="rounded-none font-body text-xs capitalize" style={{ borderColor: statusColor, color: statusColor }}>
                      {STATUS_CONFIG[status]?.label}
                    </Badge>
                  </div>
                  {canEdit && !isEditing && (
                    <Button data-testid={`edit-progress-${day.day}`} variant="outline" size="sm" onClick={() => {
                      setEditDay(day.day);
                      setEditStatus(status);
                      setEditObs(prog?.observations || '');
                      setEditSales(prog?.sales_count || 0);
                    }} className="rounded-none font-body text-xs">Update</Button>
                  )}
                </div>
                {prog?.observations && !isEditing && (
                  <p className="font-body text-sm text-[#4B5563] mt-2 pl-8">{prog.observations}</p>
                )}
                {isEditing && (
                  <div className="mt-4 pl-8 space-y-3 border-t border-[#E5E7EB] pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Status</Label>
                        <Select value={editStatus} onValueChange={setEditStatus}>
                          <SelectTrigger className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                          <SelectContent className="rounded-none">
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="failed">Failed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {day.target && (
                        <div>
                          <Label className="font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Sales Count</Label>
                          <Input type="number" min={0} value={editSales} onChange={e => setEditSales(parseInt(e.target.value) || 0)} className="mt-1 rounded-none" />
                        </div>
                      )}
                    </div>
                    <div>
                      <Label className="font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Observations</Label>
                      <Textarea value={editObs} onChange={e => setEditObs(e.target.value)} className="mt-1 rounded-none" rows={3} placeholder="Add observations..." />
                    </div>
                    <div className="flex gap-2">
                      <Button data-testid={`save-progress-${day.day}`} onClick={handleProgressUpdate} className="rounded-none bg-[#0047FF] hover:bg-[#0036CC] text-white font-body text-xs">Save</Button>
                      <Button variant="outline" onClick={() => setEditDay(null)} className="rounded-none font-body text-xs">Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </TabsContent>

        {/* Remarks Tab */}
        <TabsContent value="remarks" className="mt-4 space-y-4">
          {canEdit && (
            <form onSubmit={handleRemarkSubmit} className="border border-[#E5E7EB] p-4 space-y-3">
              <h3 className="font-heading text-base font-semibold text-[#0A0A0A]">Add Remark</h3>
              <div className="grid grid-cols-2 gap-4">
                <Select value={remarkType} onValueChange={setRemarkType}>
                  <SelectTrigger data-testid="remark-type-select" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="remark">Remark</SelectItem>
                    <SelectItem value="feedback">Feedback</SelectItem>
                  </SelectContent>
                </Select>
                <Input data-testid="remark-day-input" type="number" min={1} max={16} placeholder="Day (optional)" value={remarkDay} onChange={e => setRemarkDay(e.target.value)} className="rounded-none" />
              </div>
              <Textarea data-testid="remark-content-input" value={remarkContent} onChange={e => setRemarkContent(e.target.value)} className="rounded-none" rows={3} placeholder="Write your remark or feedback..." required />
              <Button data-testid="submit-remark-button" type="submit" className="rounded-none bg-[#0047FF] hover:bg-[#0036CC] text-white font-body">Add Remark</Button>
            </form>
          )}
          <div className="space-y-3">
            {remarks.map(r => (
              <div key={r.id} data-testid={`remark-${r.id}`} className="border border-[#E5E7EB] p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-body text-sm font-semibold text-[#0A0A0A]">{r.author_name}</span>
                    <Badge variant="outline" className="rounded-none font-body text-xs capitalize">{r.remark_type}</Badge>
                    {r.day && <span className="font-body text-xs text-[#4B5563]">Day {r.day}</span>}
                  </div>
                  <span className="font-body text-xs text-[#4B5563]">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                <p className="font-body text-sm text-[#4B5563]">{r.content}</p>
              </div>
            ))}
            {remarks.length === 0 && <p className="font-body text-sm text-[#4B5563] text-center py-8">No remarks yet</p>}
          </div>
        </TabsContent>

        {/* Evaluations Tab */}
        <TabsContent value="evaluations" className="mt-4 space-y-4">
          {canEvaluate && (
            <form onSubmit={handleEvalSubmit} className="border border-[#E5E7EB] p-4 space-y-3">
              <h3 className="font-heading text-base font-semibold text-[#0A0A0A]">New Evaluation</h3>
              <div>
                <Label className="font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Day</Label>
                <Input data-testid="eval-day-input" type="number" min={1} max={16} value={evalDay} onChange={e => setEvalDay(e.target.value)} className="mt-1 rounded-none w-32" required />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {EVAL_CRITERIA.map(c => (
                  <div key={c.key}>
                    <Label className="font-body text-xs font-semibold text-[#4B5563]">{c.label}</Label>
                    <Select value={String(evalScores[c.key] || '')} onValueChange={v => setEvalScores(p => ({ ...p, [c.key]: parseInt(v) }))}>
                      <SelectTrigger data-testid={`eval-${c.key}`} className="mt-1 rounded-none"><SelectValue placeholder="1-5" /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        {[1, 2, 3, 4, 5].map(n => (
                          <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Overall Status</Label>
                  <Select value={evalStatus} onValueChange={setEvalStatus}>
                    <SelectTrigger data-testid="eval-status-select" className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="pass">Pass</SelectItem>
                      <SelectItem value="fail">Fail</SelectItem>
                      <SelectItem value="retrain">Retrain</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Textarea data-testid="eval-notes-input" value={evalNotes} onChange={e => setEvalNotes(e.target.value)} className="rounded-none" rows={2} placeholder="Notes..." />
              <Button data-testid="submit-eval-button" type="submit" className="rounded-none bg-[#0047FF] hover:bg-[#0036CC] text-white font-body">Submit Evaluation</Button>
            </form>
          )}
          <div className="space-y-3">
            {evaluations.map(ev => {
              const avg = EVAL_CRITERIA.length > 0
                ? (EVAL_CRITERIA.reduce((sum, c) => sum + (ev.scores?.[c.key] || 0), 0) / EVAL_CRITERIA.length).toFixed(1)
                : 0;
              return (
                <div key={ev.id} data-testid={`evaluation-${ev.id}`} className="border border-[#E5E7EB] p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="font-body text-sm font-semibold text-[#0A0A0A]">{ev.evaluator_name}</span>
                      <span className="font-body text-xs text-[#4B5563]">Day {ev.day}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-body text-sm font-semibold text-[#0A0A0A]">Avg: {avg}/5</span>
                      <Badge className={`rounded-none font-body text-xs ${ev.overall_status === 'pass' ? 'bg-[#16A34A] text-white' : ev.overall_status === 'fail' ? 'bg-[#DC2626] text-white' : 'bg-[#F59E0B] text-white'}`}>
                        {ev.overall_status?.toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                    {EVAL_CRITERIA.map(c => (
                      <div key={c.key} className="text-center border border-[#E5E7EB] p-2">
                        <p className="font-body text-xs text-[#4B5563] mb-1">{c.label.split(' ')[0]}</p>
                        <p className="font-heading text-lg font-bold text-[#0A0A0A]">{ev.scores?.[c.key] || '-'}</p>
                      </div>
                    ))}
                  </div>
                  {ev.notes && <p className="font-body text-sm text-[#4B5563] mt-2">{ev.notes}</p>}
                </div>
              );
            })}
            {evaluations.length === 0 && <p className="font-body text-sm text-[#4B5563] text-center py-8">No evaluations yet</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
